"use client";
import CloseIcon from "@/assets/icons/popover-close.svg";
import { Button, ConfigProvider, Modal } from "antd";
import { createRoot } from "react-dom/client";

type ConfirmModalProps = {
  open: boolean;
  title: string;
  content: string;
  continueText?: string;
  backText?: string;
  onContinue: () => void;
  onBack: () => void;
};

export function ConfirmModal(props: ConfirmModalProps) {
  const {
    open,
    title,
    content,
    continueText = "Continue",
    backText = "Back",
    onContinue,
    onBack,
  } = props;

  return (
    <Modal
      open={open}
      closable={false}
      centered
      maskClosable={false}
      footer={null}
      classNames={{
        content:
          "w-[360px] px-6! py-8! rounded-3xl! bg-white shadow-[6px_6px_48px_0_rgba(32,32,32,0.08)]!",
        footer: "flex justify-start",
      }}
    >
      <div>
        <p className="text-center text-[20px] text-[#000] font-medium not-italic leading-normal">
          {title}
        </p>

        <p className="mt-2 text-graphite text-sm font-normal leading-normal">
          {content}
        </p>

        <div className="mt-5 flex gap-3">
          <ConfigProvider
            theme={{
              components: {
                Button: {
                  controlHeight: 40,
                  fontWeight: 500,
                  fontSize: 14,
                  colorText: "var(--color-graphite)",
                  borderRadius: 24,
                  colorPrimary: "#5251f6",
                  colorPrimaryHover: "#4E4DEA",
                  colorPrimaryActive: "#4241C5",
                },
              },
            }}
          >
            <Button
              type="text"
              className="flex-1 bg-[rgba(0,0,0,0.04)]!"
              onClick={onBack}
            >
              {backText || "Back"}
            </Button>
            <Button type="primary" className="flex-1" onClick={onContinue}>
              {continueText || "Continue"}
            </Button>
          </ConfigProvider>
        </div>

        <CloseIcon
          className="absolute top-4 right-4 w-5 h-5 text-gray-secondary cursor-pointer"
          onClick={onBack}
          aria-label="Close"
        />
      </div>
    </Modal>
  );
}

export type ConfirmOptions = Omit<
  ConfirmModalProps,
  "open" | "onContinue" | "onBack"
>;

export const confirmModal = {
  confirm: (options: ConfirmOptions): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (typeof document === "undefined") {
        resolve();
        return;
      }

      const container = document.createElement("div");
      document.body.appendChild(container);
      const root = createRoot(container);

      const render = (open: boolean) => {
        root.render(
          <ConfirmModal
            open={open}
            title={options.title}
            content={options.content}
            continueText={options.continueText}
            backText={options.backText}
            onContinue={handleContinue}
            onBack={handleBack}
          />
        );
      };

      const destroy = () => {
        // 先触发关闭动画
        render(false);
        // 等待动画完成后再销毁
        setTimeout(() => {
          root.unmount();
          if (container.parentNode) {
            container.parentNode.removeChild(container);
          }
        }, 300); // antd Modal 默认动画时间
      };

      const handleContinue = () => {
        destroy();
        resolve();
      };

      const handleBack = () => {
        destroy();
        reject();
      };

      // 先渲染为 closed 状态，然后在下一帧切换为 open，以触发动画
      render(false);
      requestAnimationFrame(() => {
        render(true);
      });
    });
  },
};

export default ConfirmModal;
